import { getDefined } from '@base/defined';
import { routes } from '@base/settings';
import { buildUrl, joinPaths } from './buildUrl';
import type { CharacterfileSchema } from '@base/config/schema';
import type { CharacterfileSchemaV2 } from '@base/config/v2/schema';

export const getProjectDashboardUrl = (
  projectId?: string,
): string | undefined => {
  if (!projectId) return undefined;

  return buildUrl(
    getDefined('PUBLIC_DASHBOARD_APP_URL'),
    routes.agentsDashboardPage,
    {
      pathParams: { projectId },
    },
  );
};

export type AgentSocialLink = {
  type: 'x' | 'github';
  url: string;
  label: string;
  username: string;
};

export const getAgentSocialLinksFromCharacterfile = (
  characterFile?: CharacterfileSchema,
): AgentSocialLink[] => {
  if (!characterFile?.settings?.secrets) return [];
  const { TWITTER_USERNAME, GITHUB_OWNER, GITHUB_REPO } =
    characterFile.settings.secrets;

  const links: AgentSocialLink[] = [];

  if (TWITTER_USERNAME) {
    links.push({
      label: 'X (Twitter)',
      type: 'x',
      url: buildUrl('https://x.com', TWITTER_USERNAME.replace('@', '')),
      username: TWITTER_USERNAME.replace('@', ''),
    });
  }

  if (GITHUB_OWNER && GITHUB_REPO) {
    links.push({
      label: 'GitHub',
      type: 'github',
      url: buildUrl('https://github.com', joinPaths(GITHUB_OWNER, GITHUB_REPO)),
      username: GITHUB_OWNER,
    });
  }

  return links;
};

export const getAgentSocialLinksFromCharacterfileV2 = (
  characterFile?: CharacterfileSchemaV2,
): AgentSocialLink[] => {
  if (!characterFile?.settings?.secrets) return [];
  const { TWITTER_USERNAME, GITHUB_OWNER, GITHUB_REPO } =
    characterFile.settings.secrets;

  const links: AgentSocialLink[] = [];

  if (TWITTER_USERNAME) {
    links.push({
      label: 'X (Twitter)',
      type: 'x',
      url: buildUrl('https://x.com', TWITTER_USERNAME.replace('@', '')),
      username: TWITTER_USERNAME.replace('@', ''),
    });
  }

  if (GITHUB_OWNER && GITHUB_REPO) {
    links.push({
      label: 'GitHub',
      type: 'github',
      url: buildUrl('https://github.com', joinPaths(GITHUB_OWNER, GITHUB_REPO)),
      username: GITHUB_OWNER,
    });
  }

  return links;
};

export const createUrlFromHostname = (hostname: string) => {
  const urlString = hostname.match(/^https?:\/\//i)
    ? hostname
    : `https://${hostname}`;

  const url = new URL(urlString);
  url.protocol = 'https:';

  return url.toString();
};

export const hasValidRedirectionUrl = () => {
  const envVarName = 'PUBLIC_REDIRECT_URL_UNAUTHENTICATED';
  const url = getDefined(envVarName);

  try {
    new URL(url);
    return true;
  } catch (e) {
    console.warn(`Expected a valid ${envVarName} but got ${typeof envVarName}`);
    return false;
  }
};

type PersonaGenRoutes = '/assistant/stream' | '/generate';

export type PersonaGenApiVersions = 'v1' | 'v2';

export const getPersonaGenEndpoint = ({
  route,
  version = 'v1',
}: {
  route: PersonaGenRoutes;
  version?: PersonaGenApiVersions;
}) =>
  buildUrl(
    getDefined('PUBLIC_PERSONA_GENERATOR_API_URL'),
    joinPaths(version, route),
  );
